import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ArrowLeft } from "lucide-react";

import { PersonaReportDataProvider } from "../context/PersonaReportDataContext";
import { getUserFromToken } from "../utils/jwtUtils";
import CrossReportBanner from "./CrossReportBanner";
import DownloadPdfButton from "./DownloadPdfButton";
import PersonaReport from "./PersonaReport";
import PersonaTrends from "./PersonaTrends";

const TABS = ["Report", "Trends"];

/**
 * Full report view. Rendered inside PersonaReportDataProvider so every tab reads its data from the
 * store (fetched once on entry) instead of refetching on tab click.
 */
const FullReportView = ({ reportId, user, navigate }) => {
  const [activeTab, setActiveTab] = useState("Report");

  const renderTab = () => {
    switch (activeTab) {
      case "Report":
        return <PersonaReport reportId={reportId} onReset={() => navigate("/persona")} />;
      case "Trends":
        return <PersonaTrends user={user} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen pt-10 pb-16">
      <div className="container mx-auto p-6">
        {/* Back */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate("/persona")}
            className="flex items-center gap-2 text-sm text-on-surface-variant hover:text-primary transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            New report
          </button>
          {activeTab === "Report" && <DownloadPdfButton reportType="persona" reportId={reportId} />}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-between gap-4 mb-8">
          <div className="flex gap-1 p-1 bg-surface-container-low rounded-xl border border-surface-container-high overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 whitespace-nowrap py-3 px-4 rounded-lg text-sm font-semibold transition-all ${
                  activeTab === tab
                    ? "bg-gradient-to-br from-primary to-primary-container text-on-primary shadow-lg"
                    : "text-on-surface-variant hover:text-on-surface hover:bg-surface-container-high"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        {renderTab()}

        <CrossReportBanner currentReport="persona" />
      </div>
    </div>
  );
};

const PersonaVisibilityReport = ({ trendsOnly = false }) => {
  const { report_id } = useParams();
  const navigate = useNavigate();

  const user = (() => {
    const t = localStorage.getItem("accessToken");
    return t ? getUserFromToken(t) : null;
  })();

  /* ── Trends-only view (/persona/trends) ─────────────────────────────── */
  if (trendsOnly) {
    return (
      <div className="min-h-screen pt-10 pb-16">
        <div className="container mx-auto p-6">
          <button
            onClick={() => navigate("/persona")}
            className="flex items-center gap-2 text-sm text-on-surface-variant hover:text-primary transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Persona Visibility
          </button>
          <PersonaTrends user={user} />
        </div>
      </div>
    );
  }

  /* ── Full report view (/persona/report/:report_id) ──────────────────── */
  return (
    <PersonaReportDataProvider reportId={report_id}>
      <FullReportView reportId={report_id} user={user} navigate={navigate} />
    </PersonaReportDataProvider>
  );
};

export default PersonaVisibilityReport;
